/**
 * Contexto de autenticação + hook de consumo.
 *
 * Separado de `useAuth.tsx` (que exporta o componente `AuthProvider`) para que
 * cada arquivo exporte apenas uma natureza de símbolo — exigência de
 * `react-refresh/only-export-components`. Nenhuma regra de negócio mudou:
 * mesmo contexto, mesmo hook, mesmo erro quando usado fora do provider.
 */
import { createContext, useContext } from 'react';
import type { Session } from '@supabase/supabase-js';

export interface AuthUser {
  id: string;
  auth_user_id: string;
  name: string;
  email: string;
  role: string;
  clinic_id: string;
  crmv: string | null;
  specialization: string | null;
  avatar_url: string | null;
}

export interface AuthContextValue {
  session: Session | null;
  user: AuthUser | null;
  clinicName: string;
  clinicLogoUrl: string;
  isLoading: boolean;
  isAuthenticated: boolean;
  isSessionReady: boolean;
  refreshUser: () => Promise<boolean>;
  signOut: () => Promise<void>;
}

/**
 * Nome em minúsculas por design: o contexto não é um componente React, e o
 * identificador deixa isso explícito no ponto de uso (`authContext.Provider`).
 */
export const authContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(authContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}